import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { CgPen, CgTrash } from "react-icons/cg";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import CustomModal from "../Commaon/CustomModal";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import SearchableMultiSelectModal from "../Commaon/SearchableMultiSelectModal";

const getRows = (data) => {
  if (Array.isArray(data?.r)) return data.r;
  if (Array.isArray(data?.r?.data)) return data.r.data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (value.$oid) return value.$oid;
    if (value._id) return normalizeId(value._id);
    if (value.id) return normalizeId(value.id);
    if (typeof value.toString === "function") {
      const stringValue = value.toString();
      return stringValue === "[object Object]" ? "" : stringValue;
    }
  }
  return String(value);
};

const emptyForm = {
  groupName: "",
  groupType: "test",
  itemIds: [],
  isActive: true,
};

const GroupingsList = () => {
  const [groups, setGroups] = useState([]);
  const [tests, setTests] = useState([]);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [formModal, setFormModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [errors, setErrors] = useState({});

  const showSnack = (message, type = "success") => {
    setSnack({ notification: { message, type } });
  };

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        `/admin/grouping?page=${pageNo}&limit=${limit}&search=${encodeURIComponent(query.trim())}`,
        { method: "GET" }
      );
      setGroups(getRows(response.data));
      setTotalRecords(response.data?.tr || 0);
    } catch (error) {
      setGroups([]);
      setTotalRecords(0);
      showSnack("Error fetching groups", "error");
    } finally {
      setLoading(false);
    }
  }, [pageNo, limit, query]);

  const fetchOptions = useCallback(async () => {
    try {
      const [testsResponse, packagesResponse] = await Promise.all([
        AuthInterceptor("/admin/test?page=1&limit=500", { method: "GET" }),
        AuthInterceptor("/admin/package?page=1&limit=500", { method: "GET" }),
      ]);
      setTests(getRows(testsResponse.data));
      setPackages(getRows(packagesResponse.data));
    } catch (error) {
      setTests([]);
      setPackages([]);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    setPageNo(1);
  }, [query]);

  const options = useMemo(
    () =>
      form.groupType === "package"
        ? packages.map((pkg) => ({
            value: normalizeId(pkg._id),
            label: pkg.title || pkg.name || normalizeId(pkg._id),
          }))
        : tests.map((test) => ({
        value: normalizeId(test._id),
        label: test.title || test.name || normalizeId(test._id),
          })),
    [form.groupType, tests, packages]
  );

  const resetForm = () => {
    setForm(emptyForm);
    setEditId(null);
    setErrors({});
  };

  const openAdd = () => {
    resetForm();
    setFormModal(true);
  };

  const openEdit = (group) => {
    setEditId(group._id);
    const groupType = group.groupType || (group.packageIds?.length ? "package" : "test");
    setForm({
      groupName: group.groupName || "",
      groupType,
      itemIds: (group.itemIds || (groupType === "package" ? group.packageIds : group.testIds) || []).map(normalizeId),
      isActive: group.isActive !== false,
    });
    setErrors({});
    setFormModal(true);
  };


  const validateForm = () => {
    const nextErrors = {};
    if (!form.groupName.trim()) nextErrors.groupName = "Group name is required";
    if (!form.itemIds.length) {
      nextErrors.items = `Select at least one ${form.groupType}`;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitForm = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const response = await AuthInterceptor(
        editId ? `/admin/grouping/${editId}` : "/admin/grouping",
        {
          method: editId ? "PUT" : "POST",
          body: JSON.stringify({
            ...form,
            testIds: form.groupType === "test" ? form.itemIds : [],
            packageIds: form.groupType === "package" ? form.itemIds : [],
          }),
        }
      );
      if ([200, 201].includes(response.data?.s)) {
        showSnack(editId ? "Group updated successfully" : "Group added successfully");
        setFormModal(false);
        resetForm();
        fetchGroups();
      } else {
        showSnack(response.data?.m || "Error saving group", "error");
      }
    } catch (error) {
      showSnack("Error saving group", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (group) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/grouping/${group._id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: !group.isActive }),
      });
      if (response.data?.s === 200) {
        showSnack("Group status updated");
        fetchGroups();
      } else {
        showSnack(response.data?.m || "Error updating status", "error");
      }
    } catch (error) {
      showSnack("Error updating status", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteGroup = async () => {
    if (!deleteId) return;
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/grouping/${deleteId}`, {
        method: "DELETE",
      });
      if (response.data?.s === 200) {
        showSnack("Group deleted successfully");
        fetchGroups();
      } else {
        showSnack(response.data?.m || "Error deleting group", "error");
      }
    } catch (error) {
      showSnack("Error deleting group", "error");
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  const totalPages = Math.ceil(totalRecords / limit) || 1;

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h2 className="mb-2 mb-md-0">Grouping</h2>
          <select
            className="form-select form-select-sm w-auto"
            value={limit}
            onChange={(e) => {
              setPageNo(1);
              setLimit(Number(e.target.value));
            }}
          >
            {[5, 10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <div className="d-flex align-items-center gap-2">
            <input
              className="form-control"
              style={{ width: "40vw" }}
              placeholder="Search groups..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="btn btn-outline-custom">Search</button>
          </div>
          <button className="btn btn-outline-custom" onClick={openAdd}>
            Add Group
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th>Sr.</th>
                <th>Group Name</th>
                <th>Type</th>
                <th>Selected Items</th>
                <th>Items</th>
                <th>Status</th>
                <th style={{ width: "130px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                groups.map((group, index) => (
                  <tr key={group._id}>
                    <td>{(pageNo - 1) * limit + index + 1}</td>
                    <td>{group.groupName || "-"}</td>
                    <td>{group.groupType === "package" ? "Package" : "Test"}</td>
                    <td className="text-truncate" title={(group.items || []).map((item) => item.title).join(", ")}>
                      {(group.items || []).map((item) => item.title).join(", ") || "-"}
                    </td>
                    <td>{group.itemCount ?? 0}</td>
                    <td>
                      {group.isActive ? (
                        <span className="badge bg-success" role="button" onClick={() => toggleStatus(group)}>
                          Active
                        </span>
                      ) : (
                        <span className="badge bg-secondary" role="button" onClick={() => toggleStatus(group)}>
                          Inactive
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-custom" onClick={() => openEdit(group)}>
                          <CgPen size={16} />
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteId(group._id)}>
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              {!loading && groups.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    No groups found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center m-3 gap-2">
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
            disabled={pageNo === 1}
          >
            Prev
          </button>
          <span className="mx-2">
            Page {pageNo} of {totalPages}
          </span>
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.min(prev + 1, totalPages))}
            disabled={pageNo >= totalPages}
          >
            Next
          </button>
        </div>
      </div>

      <Modal show={formModal} onHide={() => setFormModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editId ? "Edit Group" : "Add Group"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={submitForm}>
            <div className="mb-3">
              <label className="form-label">Group Name</label>
              <input
                className={`form-control ${errors.groupName ? "is-invalid" : ""}`}
                value={form.groupName}
                onChange={(e) => setForm((prev) => ({ ...prev, groupName: e.target.value }))}
                disabled={loading}
              />
              {errors.groupName && <div className="invalid-feedback">{errors.groupName}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label">Select Type</label>
              <select
                className="form-select"
                value={form.groupType}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    groupType: e.target.value,
                    itemIds: [],
                  }))
                }
                disabled={loading}
              >
                <option value="test">Test</option>
                <option value="package">Package</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">{form.groupType === "package" ? "Packages" : "Tests"}</label>
              <SearchableMultiSelectModal
                title={`Select ${form.groupType === "package" ? "Packages" : "Tests"}`}
                options={options}
                selectedValues={form.itemIds}
                onChange={(itemIds) => setForm((prev) => ({ ...prev, itemIds }))}
                buttonLabel={`Select ${form.groupType === "package" ? "packages" : "tests"}`}
                emptyLabel="No items selected"
                disabled={loading}
              />
              {errors.items && <div className="text-danger mt-1">{errors.items}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={form.isActive ? "true" : "false"}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.value === "true" }))}
                disabled={loading}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {editId ? "Update Group" : "Add Group"}
            </button>
          </form>
        </Modal.Body>
      </Modal>

      <CustomModal
        show={Boolean(deleteId)}
        title="Confirm Delete"
        message="Are you sure you want to delete this group?"
        onConfirm={deleteGroup}
        onCancel={() => setDeleteId(null)}
      />
    </>
  );
};

export default GroupingsList;
